import React, { useEffect, useState } from 'react';
import { Plus, Minus, TrendingUp, TrendingDown, BarChart3, PoundSterling, Eye, Users, Target, Zap, PieChart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import LearningContent from '../../components/Dashboard/LearningContent';

const fetchLivePrice = async (symbol) => {
  const baseSymbol = symbol.endsWith('.L') ? symbol.replace('.L', '') : symbol;
  const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/market-data/company-profile/?symbol=${baseSymbol}`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data && data.price !== undefined) {
      return {
        price: data.price,
        previous_close: data.previous_close,
        today_change: data.today_change,
        today_change_percent: data.today_change_percent,
      };
    }
  } catch (e) {}
  return null;
};

const HoldingCard = ({ holding, formatCurrency, onLiveUpdate }) => {
  const [live, setLive] = useState({
    price: holding.current_price,
    previous_close: holding.previous_close,
    today_change: holding.today_change,
    today_change_percent: holding.today_change_percent,
  });
  const [calc, setCalc] = useState({
    current_value: holding.current_value,
    unrealized_gain_loss: holding.unrealized_gain_loss,
    unrealized_gain_loss_percent: holding.unrealized_gain_loss_percent,
  });

  useEffect(() => {
    let isMounted = true;
    const updateLive = async () => {
      const data = await fetchLivePrice(holding.symbol);
      if (data && isMounted) {
        setLive(data);
        // Recalculate values
        const current_price = data.price;
        const current_value = holding.quantity * current_price;
        const unrealized_gain_loss = current_value - (holding.quantity * holding.average_price);
        const unrealized_gain_loss_percent = (holding.quantity * holding.average_price) > 0 ? (unrealized_gain_loss / (holding.quantity * holding.average_price)) * 100 : 0;
        const newCalc = { current_value, unrealized_gain_loss, unrealized_gain_loss_percent };
        setCalc(newCalc);
        if (onLiveUpdate) {
          onLiveUpdate(holding.symbol, data, newCalc);
        }
      }
    };
    updateLive();
    const interval = setInterval(updateLive, 25000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [holding.symbol, holding.quantity, holding.average_price]);

  return (
    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{holding.symbol}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{holding.name}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {formatCurrency(calc.current_value)}
            </p>
            <p className={`text-sm font-medium ${
              (calc.unrealized_gain_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {(calc.unrealized_gain_loss || 0) >= 0 ? '+' : ''}{formatCurrency(calc.unrealized_gain_loss)}
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Quantity</p>
            <p className="font-medium text-gray-900 dark:text-white">{holding.quantity}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Avg Price</p>
            <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(holding.average_price)}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Current</p>
            <span className={`font-medium ${
              live.today_change > 0 ? 'text-green-600' : live.today_change < 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'
            }`}>
              {formatCurrency(live.price)}
            </span>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          <span>Today's Change: </span>
          {live.today_change !== undefined && live.today_change !== null ? (
            <span className={live.today_change > 0 ? 'text-green-600' : live.today_change < 0 ? 'text-red-600' : ''}>
              {live.today_change > 0 ? '+' : ''}{live.today_change?.toFixed(2)}
              {live.today_change_percent !== undefined && live.today_change_percent !== null ? ` (${live.today_change_percent > 0 ? '+' : ''}${live.today_change_percent?.toFixed(2)}%)` : ''}
            </span>
          ) : 'N/A'}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [accountSummary, setAccountSummary] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [liveHoldings, setLiveHoldings] = useState({});

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        // Try to fetch both account summary and portfolio
        await Promise.all([
          fetchAccountSummary(),
          fetchPortfolio()
        ]);
      } catch (error) {
        console.error('Dashboard initialization error:', error);
        // If both fail, show a more helpful error message
        setError('Unable to load dashboard data. Please refresh the page or try again later.');
      }
    };
    
    initializeDashboard();
    setLiveHoldings({});
  }, []);

  const fetchAccountSummary = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await api.get('/trading/account/summary/');
      if (response.data.success) {
        setAccountSummary(response.data.data);
        setError(''); // Clear any previous errors
      } else {
        throw new Error(response.data.error || 'Failed to load account summary');
      }
    } catch (err) {
      console.error('Account summary error:', err);
      
      // For first-time users, retry once after a short delay
      if (retryCount === 0 && (err.response?.status === 500 || err.response?.status === 404)) {
        console.log('Retrying account summary fetch for new user...');
        setTimeout(() => {
          fetchAccountSummary(1);
        }, 1000);
        return;
      }
      
      // Don't set error if this is a retry attempt
      if (retryCount === 0) {
        setError('Failed to load account summary');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolio = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await api.get('/trading/portfolio/');
      if (response.data.success) {
        setPortfolio(response.data.data);
        setLiveHoldings({});
        setError(''); // Clear any previous errors
      } else {
        throw new Error(response.data.error || 'Failed to load portfolio holdings');
      }
    } catch (err) {
      console.error('Portfolio error:', err);
      
      // For first-time users, retry once after a short delay
      if (retryCount === 0 && (err.response?.status === 500 || err.response?.status === 404)) {
        console.log('Retrying portfolio fetch for new user...');
        setTimeout(() => {
          fetchPortfolio(1);
        }, 1000);
        return;
      }
      
      // Don't set error if this is a retry attempt
      if (retryCount === 0) {
        setError('Failed to load portfolio holdings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLiveUpdate = (symbol, live, calc) => {
    setLiveHoldings(prev => {
      const updated = { ...prev, [symbol]: { ...live, ...calc } };
      if (portfolio?.holdings) {
        const merged = portfolio.holdings.map(h => ({
          ...h,
          ...(updated[h.symbol] || {})
        }));
        const portfolio_value = merged.reduce((sum, h) => sum + (h.current_value || 0), 0);
        const todays_change = merged.reduce((sum, h) => sum + (h.today_change !== undefined ? h.today_change * h.quantity : 0), 0);
        const total_pnl = merged.reduce((sum, h) => sum + (h.unrealized_gain_loss || 0), 0);
        setAccountSummary(prevSummary => {
          const cash_balance = prevSummary?.cash_balance || 0;
          return {
            ...prevSummary,
            portfolio_value,
            todays_change,
            total_pnl,
            account_value: portfolio_value + cash_balance,
          };
        });
      }
      return updated;
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0.00%';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 mb-4">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-800 mb-2">Dashboard Error</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setError('');
              setLoading(true);
              fetchAccountSummary();
              fetchPortfolio();
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Card component for summary
  const SummaryCard = ({ title, value, icon: Icon, colorClass, subtitle, trendValue, trendLabel, valueStyle }) => (
    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex items-center">
      <div className="flex-shrink-0 mr-4">
        <Icon className={`h-8 w-8 ${colorClass}`} />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        <p className={`text-xl font-bold text-gray-900 dark:text-white ${colorClass}`} style={valueStyle}>{value}</p>
        {subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
        )}
        {trendValue && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{trendLabel}: {trendValue}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Welcome back! Here's an overview of your portfolio performance.
          </p>
        </div>
      </div>

      {/* Account Summary as four separate cards */}
      {accountSummary && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="Account Value"
            value={formatCurrency(accountSummary.account_value)}
            icon={PoundSterling}
            colorClass="text-blue-500"
            subtitle="Total portfolio worth"
            trendValue={formatCurrency(Math.abs(accountSummary.todays_change))}
            trendLabel="vs yesterday"
          />
          <SummaryCard
            title="Today's Change"
            value={formatCurrency(accountSummary.todays_change)}
            icon={accountSummary.todays_change >= 0 ? TrendingUp : TrendingDown}
            colorClass={accountSummary.todays_change >= 0 ? 'text-green-500' : 'text-red-500'}
            valueStyle={{ color: accountSummary.todays_change >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}
            subtitle="Market performance"
            trendValue={formatPercentage((accountSummary.todays_change / (accountSummary.account_value - accountSummary.todays_change)) * 100)}
            trendLabel="24h change"
          />
          <SummaryCard
            title="Total P&L"
            value={formatCurrency(accountSummary.total_pnl)}
            icon={BarChart3}
            colorClass={accountSummary.total_pnl >= 0 ? 'text-green-500' : 'text-red-500'}
            valueStyle={{ color: accountSummary.total_pnl >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}
            subtitle="Unrealized gains/losses"
            trendValue={formatPercentage((accountSummary.total_pnl / (accountSummary.account_value - accountSummary.total_pnl)) * 100)}
            trendLabel="overall return"
          />
          <SummaryCard
            title="Cash Balance"
            value={formatCurrency(accountSummary.cash_balance)}
            icon={PoundSterling}
            colorClass="text-yellow-500"
            subtitle="Available for trading"
          />
        </div>
      )}

      {/* Portfolio Holdings */}
      <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Portfolio Holdings</h2>
          {portfolio?.holdings && portfolio.holdings.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {portfolio.holdings.map((holding) => (
                <HoldingCard key={holding.symbol} holding={holding} formatCurrency={formatCurrency} onLiveUpdate={handleLiveUpdate} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No holdings yet.</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Start trading to build your portfolio!</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Quick Actions</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Access your most important features</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <button
              className="group relative flex flex-col items-center p-6 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all duration-200 hover:scale-105 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20"
              onClick={() => navigate('/portfolio')}
            >
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-200 dark:group-hover:bg-primary-900/50 transition-colors duration-200">
                <BarChart3 className="w-6 h-6 text-primary-600" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Portfolio</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">View holdings</p>
              <div className="mt-2 text-xs text-primary-600 dark:text-primary-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                My Portfolio
              </div>
            </button>
            
            <button
              className="group relative flex flex-col items-center p-6 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all duration-200 hover:scale-105 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
              onClick={() => navigate('/research?region=eu')}
            >
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors duration-200">
                <Search className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Research</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Market analysis</p>
              <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                European Markets
              </div>
            </button>
            
            <button
              className="group relative flex flex-col items-center p-6 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-green-300 dark:hover:border-green-600 hover:shadow-md transition-all duration-200 hover:scale-105 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20"
              onClick={() => navigate('/analytics?region=eu')}
            >
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors duration-200">
                <BarChart3 className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Analytics</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Performance insights</p>
              <div className="mt-2 text-xs text-green-600 dark:text-green-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                EU Analytics
              </div>
            </button>
            
            <button
              className="group relative flex flex-col items-center p-6 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md transition-all duration-200 hover:scale-105 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20"
              onClick={() => navigate('/profile')}
            >
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors duration-200">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Profile</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Account settings</p>
              <div className="mt-2 text-xs text-purple-600 dark:text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                Update Profile
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Learning Content */}
      <LearningContent />
    </div>
  );
};

export default Dashboard; 